// Approximate build years and a type for every Plate Viewer vehicle, keyed by
// config id (the same ids the API catalogue and stored_configs use). The
// catalogue itself carries no vehicle facts, so this table is the local source
// until the admin tool can hold them.
//
// Used for two things:
//   1. The legal dated-plate rule: a dated plate cannot make a car look newer
//      than it is, so a plate is never shown on a car whose production ended
//      well before the plate's year (see carFitsPlate).
//   2. Matching the hero render to the car the plate is on now (same model
//      family, then same make, then the same band).
//
// Years are the generation pictured, approximate. yearTo null = still built.

export type ViewerCarType =
  | 'classic'
  | 'supercar'
  | 'luxury'
  | 'performance'
  | 'suv'
  | 'everyday';

export interface ViewerCarMeta {
  /** Normalised make (lowercase letters only), e.g. 'porsche', 'rollsroyce'. */
  make: string;
  /** Matches the DVLA model text of the same model family, e.g. /\b911\b/. */
  family?: RegExp;
  yearFrom: number;
  yearTo: number | null;
  type: ViewerCarType;
  /** Extra bands this car also serves, e.g. an M3 for everyday makes. */
  alsoBands?: ViewerCarType[];
  /** Only used when the current car is the same make (never as a band pick). */
  sameMakeOnly?: boolean;
  /** false = never used as a hero (not a UK car / novelty). */
  hero?: boolean;
  /** A hypercar: the stand-in for a hypercar the catalogue lacks (F80 -> LaFerrari). */
  hyper?: boolean;
}

export const VIEWER_CAR_META: { [id: number]: ViewerCarMeta } = {
  // Classic
  661: { make: 'ferrari', family: /\b250\b/, yearFrom: 1957, yearTo: 1962, type: 'classic' }, // 250 GT Cabriolet
  662: { make: 'ferrari', family: /\b250\b/, yearFrom: 1959, yearTo: 1962, type: 'classic' }, // 250 GT SWB
  667: { make: 'ferrari', family: /\b250\b/, yearFrom: 1958, yearTo: 1960, type: 'classic' }, // 250 GT PF
  398: { make: 'lamborghini', family: /COUNTACH/, yearFrom: 1974, yearTo: 1990, type: 'classic' },
  433: { make: 'ferrari', family: /TESTAROSSA|\b512\s?TR\b/, yearFrom: 1984, yearTo: 1991, type: 'classic' },

  // Supercar
  500: { make: 'lamborghini', family: /MURCI/, yearFrom: 2001, yearTo: 2010, type: 'supercar' },
  401: { make: 'ferrari', family: /LAFERRARI/, yearFrom: 2013, yearTo: 2018, type: 'supercar', hyper: true },
  402: { make: 'mclaren', family: /\bP1\b/, yearFrom: 2013, yearTo: 2015, type: 'supercar', hyper: true },
  496: { make: 'porsche', family: /\b918\b/, yearFrom: 2013, yearTo: 2015, type: 'supercar', hyper: true },
  501: { make: 'mclaren', family: /\b675|\b650|\b720|\b750/, yearFrom: 2015, yearTo: 2017, type: 'supercar' },
  463: { make: 'lamborghini', family: /AVENTADOR/, yearFrom: 2017, yearTo: 2022, type: 'supercar' },
  399: { make: 'ferrari', family: /\bF12/, yearFrom: 2012, yearTo: 2017, type: 'supercar' },
  826: { make: 'ferrari', family: /\b458\b/, yearFrom: 2012, yearTo: 2015, type: 'supercar' },
  432: { make: 'ferrari', family: /\b488\b/, yearFrom: 2018, yearTo: 2020, type: 'supercar' }, // 488 Pista
  435: { make: 'ferrari', family: /\b488\b|\bF8\b/, yearFrom: 2018, yearTo: 2023, type: 'supercar' }, // F8 render
  265: { make: 'lamborghini', family: /HURACAN/, yearFrom: 2019, yearTo: 2024, type: 'supercar' },

  // Luxury / GT
  299: { make: 'rollsroyce', family: /WRAITH|DAWN/, yearFrom: 2013, yearTo: 2023, type: 'luxury' },
  498: { make: 'bentley', family: /CONTINENTAL/, yearFrom: 2018, yearTo: null, type: 'luxury' },
  437: { make: 'ferrari', family: /\bROMA\b|PORTOFINO/, yearFrom: 2020, yearTo: null, type: 'luxury' },
  669: { make: 'astonmartin', family: /\bDB\d/, yearFrom: 2023, yearTo: null, type: 'luxury' },
  404: { make: 'rollsroyce', family: /SPECTRE/, yearFrom: 2023, yearTo: null, type: 'luxury' },

  // Performance
  863: { make: 'bmw', family: /^1|\b1M\b|\bM2\b/, yearFrom: 2011, yearTo: 2012, type: 'performance', alsoBands: ['everyday'] },
  298: { make: 'bmw', family: /\bI8\b/, yearFrom: 2014, yearTo: 2020, type: 'performance' },
  397: { make: 'mercedes', family: /\bAMG GT|\bGT\s?R\b|\bGT\s?S\b/, yearFrom: 2017, yearTo: 2021, type: 'performance' },
  371: { make: 'audi', family: /\bRS\s?5\b|\bS5\b|\bA5\b/, yearFrom: 2017, yearTo: null, type: 'performance' },
  367: { make: 'bmw', family: /\bM8\b|^8|\b8 SERIES/, yearFrom: 2019, yearTo: null, type: 'performance' },
  301: { make: 'bmw', family: /\bZ4\b/, yearFrom: 2019, yearTo: null, type: 'performance' },
  503: { make: 'porsche', family: /\b718\b|BOXSTER|CAYMAN/, yearFrom: 2019, yearTo: null, type: 'performance' },
  364: { make: 'alpine', family: /A110/, yearFrom: 2019, yearTo: null, type: 'performance' },
  430: { make: 'porsche', family: /\b911\b|CARRERA|TURBO/, yearFrom: 2020, yearTo: null, type: 'performance' }, // 911 Turbo S
  366: { make: 'bmw', family: /\bM3\b|\bM4\b|^3|\b3 SERIES/, yearFrom: 2021, yearTo: null, type: 'performance', alsoBands: ['everyday'] },
  497: { make: 'porsche', family: /\b911\b|GT3|CARRERA/, yearFrom: 2023, yearTo: null, type: 'performance' },
  665: { make: 'alpine', family: /A110/, yearFrom: 2024, yearTo: null, type: 'performance' },
  369: { make: 'audi', family: /\bR8\b/, yearFrom: 2019, yearTo: 2024, type: 'performance' },

  // SUV
  406: { make: 'mercedes', family: /\bG\s?\d|G[- ]?CLASS|G WAGON/, yearFrom: 2013, yearTo: 2015, type: 'suv' }, // 6x6
  502: { make: 'bentley', family: /BENTAYGA/, yearFrom: 2016, yearTo: null, type: 'suv' },
  403: { make: 'rollsroyce', family: /CULLINAN/, yearFrom: 2018, yearTo: null, type: 'suv' },
  431: { make: 'mercedes', family: /\bG\s?\d|G[- ]?CLASS|G WAGON/, yearFrom: 2018, yearTo: null, type: 'suv' },
  668: { make: 'astonmartin', family: /\bDBX\b/, yearFrom: 2020, yearTo: null, type: 'suv' },
  499: { make: 'landrover', family: /DEFENDER/, yearFrom: 2020, yearTo: null, type: 'suv' },
  405: { make: 'lamborghini', family: /\bURUS\b/, yearFrom: 2022, yearTo: null, type: 'suv' },
  436: { make: 'landrover', family: /DEFENDER/, yearFrom: 1990, yearTo: 2016, type: 'suv' }, // Heritage Customs

  // Everyday
  300: { make: 'mini', yearFrom: 2014, yearTo: null, type: 'everyday' },
  434: { make: 'fiat', family: /\b500\b/, yearFrom: 2016, yearTo: null, type: 'everyday', sameMakeOnly: true },
  664: { make: 'alfaromeo', family: /GIULIA/, yearFrom: 2016, yearTo: null, type: 'everyday', sameMakeOnly: true },
  370: { make: 'audi', family: /\bRS\s?3\b|\bS3\b|\bA3\b/, yearFrom: 2021, yearTo: null, type: 'everyday' },

  // Never a hero
  504: { make: 'tesla', yearFrom: 2023, yearTo: null, type: 'everyday', hero: false }, // Cybertruck, not UK
  727: { make: 'ford', yearFrom: 2020, yearTo: null, type: 'suv', hero: false }, // US Explorer
};

/** Lowercase letters only: 'ROLLS-ROYCE' -> 'rollsroyce', 'MERCEDES-BENZ' -> 'mercedes'. */
export function normaliseMake(make: string | null | undefined): string {
  const m = (make || '').toLowerCase().replace(/[^a-z]/g, '');
  if (m.startsWith('mercedes')) return 'mercedes';
  if (m.startsWith('rangerover')) return 'landrover';
  if (m.startsWith('bac')) return 'bac'; // 'BAC MONO'
  return m;
}

// Bands for a current car with no same-make vehicle in the catalogue. Any
// make not listed here is 'everyday' (Ford, Vauxhall, VW, Toyota, Kia ...).
const MAKE_BANDS: { [make: string]: ViewerCarType } = {
  ferrari: 'supercar', lamborghini: 'supercar', mclaren: 'supercar',
  bugatti: 'supercar', pagani: 'supercar', koenigsegg: 'supercar',
  rollsroyce: 'luxury', bentley: 'luxury', astonmartin: 'luxury',
  maybach: 'luxury', maserati: 'luxury',
  porsche: 'performance', alpine: 'performance', lotus: 'performance',
  tvr: 'performance', morgan: 'performance', caterham: 'performance',
  noble: 'performance', ariel: 'performance', bac: 'performance',
  radical: 'performance', ginetta: 'performance', ultima: 'performance',
  westfield: 'performance', ktm: 'performance', rimac: 'supercar',
  landrover: 'suv', jeep: 'suv',
};

export function bandForMake(make: string | null | undefined): ViewerCarType {
  return MAKE_BANDS[normaliseMake(make)] || 'everyday';
}
